import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { manageClientsActions } from "@shared/actions";
import { systemConstants } from "@shared/constants";
import { utilities } from "@shared/helpers";
import { useAuthUser } from "@shared/hooks";

import { routeConstants } from "@app/constants";
import { AccessLevel, ResourceName } from "@app/types";

import { Stack } from "@fermions";

import { Button } from "@atoms/Button";

import PageTemplate from "@templates/PageTemplate";

import DropdownPagination from "@components/molecules/DropdownPagination";
import ClientsTable from "@components/organisms/ClientDataTable";
import SearchTextInput from "@components/organisms/SearchTextInput";

const ManageClients = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authentication = useSelector(state => state.authentication);
  const manageClients = useSelector(state => state.manageClients);
  const { user } = useAuthUser();
  const [clients, setClients] = useState([]);
  const [pagination, setPagination] = useState({
    countPerPage: systemConstants.pagination.itemCountPerPage,
    pageCount: 1,
    currentPage: "Page 1",
    currentPageIndex: 0,
    pages: []
  });
  const { isKeywordInclude } = utilities;
  const [filter, setFilter] = useState({ string: "", clients: [] });

  useEffect(() => {
    dispatch(manageClientsActions.getClients(authentication.user));
    return () => {
      dispatch(manageClientsActions.resetClients());
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setFilter({ ...filter, clients: manageClients.clients });
    // eslint-disable-next-line
  }, [manageClients.clients]);

  useEffect(() => {
    if (filter.string) {
      createPagination(filter.clients);
    } else {
      createPagination(manageClients.clients);
    }
    // eslint-disable-next-line
  }, [filter.clients]);

  useEffect(() => {
    if (!filter.string) {
      createPagination(manageClients.clients);
    }
    // eslint-disable-next-line
  }, [filter.string]);

  useEffect(() => {
    if (pagination.pages.length) {
      setClients(pagination.pages[pagination.currentPageIndex].data);
    } else {
      setClients([]);
    }
  }, [pagination]);

  const createPagination = allClients => {
    const count = Math.ceil(allClients.length / pagination.countPerPage);
    const items = [];
    for (let number = 0; number < count; number++) {
      const data = allClients.slice(
        number * pagination.countPerPage,
        number * pagination.countPerPage + pagination.countPerPage
      );
      items.push({ number: number + 1, name: `Page ${number + 1}`, data });
    }

    setPagination({
      ...pagination,
      currentPage: "Page 1",
      currentPageIndex: 0,
      pageCount: count,
      pages: items
    });
  };

  const handleFilterChange = searchInput => {
    if (!searchInput) {
      setFilter({
        string: "",
        clients: manageClients.clients
      });
    } else {
      const filteredClients = manageClients.clients.filter(
        client =>
          isKeywordInclude(client.name, searchInput) ||
          (client.code && isKeywordInclude(client.code, searchInput))
      );
      setFilter({
        string: searchInput,
        clients: filteredClients
      });
    }
  };

  const handleAddClient = () => {
    navigate(routeConstants.addClient);
  };

  const handleManageEntities = client => {
    if (!user.checkAccess(ResourceName.CLIENTS, AccessLevel.UPDATE)) {
      return;
    }
    navigate(routeConstants.admin.manageClientEntities, {
      state: client
    });
  };
  const handleUpdateClient = client => {
    if (!user.checkAccess(ResourceName.CLIENTS, AccessLevel.UPDATE)) {
      return;
    }
    navigate(`/admin/clients/${client.id}/edit`);
  };

  const handleManageClientUsers = client => {
    if (!user.checkAccess(ResourceName.CLIENT_USERS, AccessLevel.READ)) {
      return;
    }
    navigate(`/admin/clients/${client.id}/users`);
  };

  const headerActions = () => {
    if (!user.checkAccess(ResourceName.CLIENTS, AccessLevel.CREATE)) {
      return <></>;
    }
    return (
      <Button
        label={t("common:ui.manageClients.addClient")}
        iconName="add"
        onClick={handleAddClient}
      />
    );
  };

  return (
    <PageTemplate
      header={{
        title: t("common:ui.manageClients.title"),
        actions: headerActions(),
        alwaysShowBreadcrumbs: true
      }}
      body={{
        header: (
          <SearchTextInput
            label={t("common:ui.manageClients.searchLabel")}
            handleChange={handleFilterChange}
          />
        ),
        primary: (
          <Stack className="client-manager" width="100" gap="300">
            {clients.length ? (
              <>
                <ClientsTable
                  clients={clients}
                  onHandleManageEntities={handleManageEntities}
                  onHandleUpdateClient={handleUpdateClient}
                  onHandleManageClientUsers={handleManageClientUsers}
                  canViewUser={user.checkAccess(
                    ResourceName.CLIENT_USERS,
                    AccessLevel.READ
                  )}
                  canUpdateClient={user.checkAccess(
                    ResourceName.CLIENTS,
                    AccessLevel.UPDATE
                  )}
                />
                <DropdownPagination
                  pagination={pagination}
                  setPagination={setPagination}
                />
              </>
            ) : (
              ""
            )}
          </Stack>
        )
      }}
      other={{
        error: manageClients.error
      }}
    />
  );
};

export default ManageClients;
